import type { WorkspaceControllerGetWorkspacesQueryParams } from '@/api/repository/repositoryComponents'
import {
	useWorkspaceControllerCreateWorkspace,
	useWorkspaceControllerGetWorkspaces,
	useWorkspaceControllerUpdateWorkspace,
	useWorkspaceControllerUpdateWorkspacePlan,
} from '@/api/repository/repositoryComponents'
import { DEFAULT_STALE_TIME } from '@/deprecated/services/constants'
import type { Workspace } from '@/shared/types'
import { useQueryClient } from '@tanstack/react-query'
import qs from 'qs'
import { toast } from 'react-hot-toast'

interface Props {
	filters?: {
		status?: WorkspaceControllerGetWorkspacesQueryParams['status']
		plan?: WorkspaceControllerGetWorkspacesQueryParams['plan']
	}
}

export const useWorkspaces = (props: Props) => {
	const queryClient = useQueryClient()

	const {
		data: initialData,
		isLoading,
		error,
	} = useWorkspaceControllerGetWorkspaces(
		{
			// @ts-expect-error: Allows filters to be passed as an object in url
			queryParams: qs.stringify(props?.filters, { skipNulls: true }),
		},
		{
			staleTime: DEFAULT_STALE_TIME,
		}
	)

	const workspaces: Array<Workspace> = initialData?.data

	const createWorkspace = useWorkspaceControllerCreateWorkspace({
		onSuccess: () => {
			queryClient.invalidateQueries()
			toast.success(`Workspace Created!`)
		},
		onError: error => {
			toast.error(error.stack.message)
		},
	})

	const updateWorkspace = useWorkspaceControllerUpdateWorkspace({
		onSuccess: () => {
			queryClient.invalidateQueries()
			toast.success(`Workspace Edited!`)
		},
		onError: error => {
			toast.error(error.stack.message)
		},
	})

	const updateWorkspacePlan = useWorkspaceControllerUpdateWorkspacePlan({
		onSuccess: () => {
			queryClient.invalidateQueries()
			toast.success(`Workspace plan updated!`)
		},
		onError: error => {
			toast.error(error.stack.message)
		},
	})

	return {
		workspaces,
		isLoading,
		createWorkspace,
		updateWorkspace,
		error,
		updateWorkspacePlan,
	}
}
